/*!
 * Copyright © 2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { EPendoCustomEvents } from '../../../core/models';
import { IAttributeListItem } from '../../../core/models/attribute.interface';
import { PendoTrackEventService } from '../../../external/services/pendo-track-event';
import { AttributeTreeNodeComponent, TreeNode } from './attribute-tree-node/attribute-tree-node.component';

/**
 * Overlay-based attribute filter component.
 *
 * Displays a toggle button that opens a CDK overlay containing an attribute tree.
 * Users can select/deselect attribute values and apply the filter.
 * Converts between the flat TreeNode model (used by the tree) and
 * the IAttributeListItem model (used by consumers).
 */
@Component({
  selector: 'fm-attribute-filter',
  templateUrl: './attribute-filter.component.html',
  styleUrls: ['./attribute-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AttributeTreeNodeComponent, MatIconModule, MatLegacyButtonModule, OverlayModule, MatDividerModule],
  standalone: true
})
export class AttributeFilterComponent {
  private readonly _pendoTrackEventService = inject(PendoTrackEventService);
  private readonly _treeNode = viewChild(AttributeTreeNodeComponent);
  readonly overlayPositions: ConnectedPosition[] = [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top' }];
  showFilter = signal(false);
  overlayWidth = signal(0);
  overlayHeight = signal(0);

  attributeItems = input<IAttributeListItem[]>([]);
  selectedAttributes = input<IAttributeListItem[]>([]);
  loading = input(false);
  showSkeleton = input(false);
  error = input(false);
  containerRef = input<ElementRef>();

  filterApplied = output<IAttributeListItem[]>();
  refreshClicked = output<void>();

  private readonly _treeModelResult = computed(() => {
    const attributeItems = this.attributeItems();
    const selectedAttributeItems = this.selectedAttributes();
    return this._buildTreeModel(attributeItems, selectedAttributeItems);
  });

  treeNodes = computed(() => {
    const { nodes } = this._treeModelResult();
    return nodes;
  });
  private readonly _expandedParentIds = computed(() => {
    const { expandedParentIds } = this._treeModelResult();
    return expandedParentIds;
  });

  /** Toggles the filter overlay open/closed, sizing it to the container element. */
  toggleFilter(): void {
    if (!this.showFilter()) {
      const container = this.containerRef();
      if (container) {
        const rect = container.nativeElement.getBoundingClientRect();
        this.overlayWidth.set(rect.width);
        this.overlayHeight.set(rect.height);
      }
      this._restoreTreeState();
      const attributesAvailable = this.treeNodes().filter(node => node.level === 1).length;
      this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.FilterPanelOpened, {
        AttributesAvailable: attributesAvailable
      });
    }
    this.showFilter.set(!this.showFilter());
  }
  /** Recalculates the virtual scroll viewport once the overlay is attached. */
  onOverlayAttached(): void {
    this._treeNode()?.checkViewportSize();
  }
  /** Handles the apply action from the tree: closes the overlay, converts selected nodes to IAttributeListItem[], and updates state. */
  onFilterApply(nodes: TreeNode[]): void {
    const selectedItemMap = new Map<string, IAttributeListItem>();

    for (const node of nodes) {
      const key = node.attributeId;
      if (!selectedItemMap.has(key)) {
        const entry: IAttributeListItem = { attributeId: node.attributeId, name: null, description: null, stringValues: [] };
        selectedItemMap.set(key, entry);
      }
      if (node.level === 0) {
        selectedItemMap.get(key).name = node.name;
      } else if (node.isChecked) {
        selectedItemMap.get(key).stringValues.push(node.name);
      }
    }
    const selectedAttributes = [...selectedItemMap.values()].filter(item => item.stringValues.length > 0);
    const attributesSelected = selectedAttributes.reduce((sum, item) => sum + item.stringValues.length, 0);
    if (attributesSelected > 0) {
      this._pendoTrackEventService.sendPendoTrackEventWhenReady(EPendoCustomEvents.AttributeFilterApplied, {
        AttributesSelected: attributesSelected,
        AttributeNamesUsed: selectedAttributes.length
      });
    }
    this.filterApplied.emit(selectedAttributes);
    this.toggleFilter();
  }

  /** Converts the hierarchical IAttributeListItem[] into a flat array of TreeNode[]. */
  private _buildTreeModel(
    attributes: IAttributeListItem[],
    selectedItems: IAttributeListItem[]
  ): { nodes: TreeNode[]; expandedParentIds: Set<string> } {
    const selectedValuesMap = new Map<string, Set<string>>();
    for (const item of selectedItems) {
      selectedValuesMap.set(item.attributeId, new Set(item.stringValues));
    }

    const nodes: TreeNode[] = [];
    const expandedParentIds = new Set<string>();

    for (const attr of attributes) {
      const selectedValues = selectedValuesMap.get(attr.attributeId);
      const parentIndex = nodes.length;

      const parentNode: TreeNode = {
        id: `${attr.attributeId}`,
        name: attr.name,
        level: 0,
        isExpandable: attr.stringValues.length > 0,
        attributeId: attr.attributeId,
        isExpanded: false,
        isChecked: false,
        isIndeterminate: false
      };
      nodes.push(parentNode);

      let checkedCount = 0;

      for (const [i, value] of attr.stringValues.entries()) {
        const isChecked = selectedValues?.has(value) ?? false;
        if (isChecked) {
          checkedCount++;
        }

        const childNode: TreeNode = {
          id: `${value}-${i}`,
          name: value,
          level: 1,
          isExpandable: false,
          attributeId: attr.attributeId,
          isExpanded: false,
          isChecked,
          isIndeterminate: false
        };
        nodes.push(childNode);
      }

      if (checkedCount > 0) {
        const allChecked = checkedCount === attr.stringValues.length;
        nodes[parentIndex] = {
          ...nodes[parentIndex],
          isExpanded: true,
          isChecked: allChecked,
          isIndeterminate: !allChecked
        };
        expandedParentIds.add(nodes[parentIndex].id);
      }
    }
    return { nodes, expandedParentIds };
  }

  /** Restores isExpanded, isChecked and isIndeterminate on all nodes from selectedAttributes (undoes any unapplied mutations). */
  private _restoreTreeState(): void {
    const selectedValuesMap = new Map<string, Set<string>>();
    for (const item of this.selectedAttributes()) {
      selectedValuesMap.set(item.attributeId, new Set(item.stringValues));
    }

    const nodes = this.treeNodes();
    const expandedParentIds = this._expandedParentIds();
    let i = 0;

    while (i < nodes.length) {
      const node = nodes[i];

      if (!node.isExpandable) {
        i++;
        continue;
      }

      node.isExpanded = expandedParentIds.has(node.id);
      const selectedValues = selectedValuesMap.get(node.attributeId);
      let checkedCount = 0;
      let j = i + 1;

      while (j < nodes.length && nodes[j].level > node.level) {
        const child = nodes[j];
        child.isChecked = selectedValues?.has(child.name) ?? false;
        child.isIndeterminate = false;
        if (child.isChecked) {
          checkedCount++;
        }
        j++;
      }

      const childCount = j - i - 1;
      if (checkedCount === childCount) {
        node.isChecked = true;
        node.isIndeterminate = false;
      } else if (checkedCount === 0) {
        node.isChecked = false;
        node.isIndeterminate = false;
      } else {
        node.isChecked = false;
        node.isIndeterminate = true;
      }

      i = j;
    }
  }
}
